import {
  Alert,
  Button,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconClock, IconFileSpreadsheet, IconLock, IconPrinter, IconTargetArrow } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { fetchEmployeeReport } from '../shared/api/kpi'
import { downloadEmployeeReportXlsx } from '../shared/api/exports'
import { useAuthStore } from '../features/auth/store'
import { AppLayout } from '../features/projects/app-shell'
import { isApiError } from '../shared/api/client'
import { formatDuration, onTimeRate } from '../features/reports/format'
import { MetricBlock, SubStat, EmptyData } from '../features/reports/metric-block'

function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0))
  return { from: from.toISOString(), to: now.toISOString() }
}

const DONUT_COLORS = ['#1F4E79', '#2E75B6', '#5e94ca', '#a8c6e3', '#f59e0b', '#22c55e', '#a855f7']

export function EmployeeReportPage() {
  const { user } = useAuthStore()
  const params = useParams<{ id: string }>()
  const employeeId = params.id ?? user?.id ?? ''
  const period = useMemo(currentMonthRange, [])

  const q = useQuery({
    queryKey: ['employee-report', employeeId, period.from, period.to],
    queryFn: () => fetchEmployeeReport(employeeId, period.from, period.to),
    enabled: !!employeeId,
    retry: false,
  })

  const exportXlsx = useMutation({
    mutationFn: () => downloadEmployeeReportXlsx(employeeId, period.from, period.to),
    onError: (e) => {
      const msg = isApiError(e) && e.response?.status === 403
        ? 'Нет доступа к этому отчёту — файл не скачан'
        : 'Не удалось сформировать файл'
      notifications.show({ color: 'red', title: 'Экспорт не выполнен', message: msg })
    },
  })

  return (
    <AppLayout>
      <Container size="lg" py="md" className="print-report">
        <Group justify="space-between" mb="lg">
          <div>
            <Text size="xs" c="blue.7" fw={700} tt="uppercase">
              Отчёт по сотруднику
            </Text>
            <Title order={2}>{q.data?.employee.full_name ?? '…'}</Title>
          </div>
          {/* Кнопки только при наличии данных: для чужого (403) отчёта их нет */}
          {q.data && (
            <Group gap="xs" className="no-print">
              <Button
                variant="default"
                leftSection={<IconFileSpreadsheet size={16} />}
                loading={exportXlsx.isPending}
                onClick={() => exportXlsx.mutate()}
              >
                Excel
              </Button>
              <Button
                variant="default"
                leftSection={<IconPrinter size={16} />}
                onClick={() => window.print()}
              >
                Печать / PDF
              </Button>
            </Group>
          )}
        </Group>

        <ReportBody query={q} />
      </Container>
    </AppLayout>
  )
}

function ReportBody({ query }: { query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchEmployeeReport>>>> }) {
  // 403 — внятный отказ (это собственный доступ сотрудника, не head'а)
  if (isApiError(query.error) && query.error.response?.status === 403) {
    return (
      <Alert color="red" icon={<IconLock size={18} />} variant="light" title="Нет доступа">
        Вы можете смотреть только свой отчёт. Отчёты других сотрудников доступны их руководителю.
      </Alert>
    )
  }
  if (query.isLoading) {
    return (
      <Center mih={300}>
        <Loader />
      </Center>
    )
  }
  if (query.error || !query.data) {
    return <Text c="red">Не удалось загрузить отчёт. Обновите страницу.</Text>
  }

  const r = query.data
  const emptyDuration = r.duration.sample_size === 0
  const onTimeTotal = r.on_time.on_time + r.on_time.overdue + r.on_time.no_due
  const hasContribution = r.contribution_by_project.length > 0

  return (
    <Stack gap="lg">
      {/* Карточки-метрики сверху: создано/выполнено/переносы/загрузка */}
      <Grid>
        <TopMetric label="Создано" value={r.created_count} />
        <TopMetric label="Выполнено" value={r.completed_count} />
        <TopMetric label="Переносы сроков" value={r.due_changes} warn={r.due_changes > 0} />
        <TopMetric label="Открыто сейчас" value={r.current_load.open} sub={`просрочено ${r.current_load.overdue}`} />
      </Grid>

      {/* ДВА РАВНОПРАВНЫХ БЛОКА */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <MetricBlock icon={<IconClock size={20} />} title="Длительность выполнения" color="blue">
            {emptyDuration ? (
              <EmptyData />
            ) : (
              <Group grow gap="xl">
                <SubStat label="Среднее" value={formatDuration(r.duration.avg_seconds)} />
                <SubStat label="Медиана" value={formatDuration(r.duration.median_seconds)} />
              </Group>
            )}
          </MetricBlock>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <MetricBlock icon={<IconTargetArrow size={20} />} title="Соблюдение срока" color="teal">
            {onTimeTotal === 0 ? (
              <EmptyData />
            ) : (
              <Group grow gap="xl">
                <SubStat label="В срок" value={String(r.on_time.on_time)} sub={onTimeRate(r.on_time.on_time, r.on_time.overdue)} />
                <SubStat label="Просрочено" value={String(r.on_time.overdue)} danger={r.on_time.overdue > 0} />
                <SubStat label="Без срока" value={String(r.on_time.no_due)} muted />
              </Group>
            )}
          </MetricBlock>
        </Grid.Col>
      </Grid>

      {/* Вклад по проектам — donut. Пусто → «нет данных», график не падает */}
      <Card withBorder p="lg" radius="md">
        <Text fw={600} mb="sm">
          Распределение вклада по проектам
        </Text>
        {!hasContribution ? (
          <EmptyData />
        ) : (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={r.contribution_by_project.map((c) => ({ name: c.project_name, value: c.completed }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                >
                  {r.contribution_by_project.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </Stack>
  )
}

function TopMetric({ label, value, sub, warn }: { label: string; value: number; sub?: string; warn?: boolean }) {
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Card withBorder p="md" radius="md">
        <Text size="28px" fw={800} c={warn && value > 0 ? 'orange.7' : undefined} style={{ fontFeatureSettings: '"tnum"' }}>
          {value}
        </Text>
        <Text size="xs" c="dimmed" mt={4}>
          {label}
        </Text>
        {sub && (
          <Text size="xs" c="dimmed">
            {sub}
          </Text>
        )}
      </Card>
    </Grid.Col>
  )
}
